import { useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { QueryKeys, queryClient } from "@/api/core";
import { useApi } from "@/hooks";
import toast from "@/toast";
import { removePageItem } from "@/utils/queries";

import type { Account, Relationship } from "@/schemas";

interface Params {
  account: Account;
}

const messages = defineMessages({
  unblockSuccess: {
    id: "accounts.unblock.success",
    defaultMessage: "You have successfully unblocked @{username}.",
  },
});

function useUnblockAccount() {
  const api = useApi();
  const intl = useIntl();

  return useMutation({
    mutationFn: (params: Params) =>
      api.post<Relationship>(`/api/v1/accounts/${params.account.id}/unblock`),
    async onMutate(variables) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const prevRelationship = queryClient.getQueryData(
        queryKey,
      ) as Relationship;
      const nextRelationship = { ...prevRelationship, blocking: false };

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, nextRelationship);

      return { queryKey, prevRelationship };
    },
    onSuccess(response, variables) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);
      queryClient.setQueryData(queryKey, response.data);

      // TODO: Bust cache for Feed, Notifications
      removePageItem(
        QueryKeys.suggestions.global,
        variables.account.id,
        (o: any, n: any) => o.account_id === n,
      );
      queryClient.invalidateQueries({
        queryKey: QueryKeys.statuses.accountId(variables.account.id),
      });

      // Render a toast
      toast.success(
        intl.formatMessage(messages.unblockSuccess, {
          username: variables.account.acct,
        }),
      );
    },
    onError(error, variables, context) {
      // Return Relationship back to its original state
      if (context) {
        queryClient.setQueryData(context.queryKey, context.prevRelationship);
      }
    },
  });
}

export { useUnblockAccount };
